
import { useState, useCallback, Dispatch, SetStateAction } from 'react';
import { AppNode, ProcessDefinition, Edge } from '../types';
// Fix: Use named import for useReactFlow from reactflow.
import { useReactFlow } from 'reactflow';
import { useToast } from '../contexts/ToastContext';

// Helper to safely deep clone nodes, handling potential circular refs gracefully if they exist
const safeDeepClone = <T>(obj: T): T => {
    try {
        return JSON.parse(JSON.stringify(obj));
    } catch (e) {
        console.error("Deep clone failed (likely circular reference). Falling back to sanitization.", e);
        if (Array.isArray(obj)) return [...obj] as any;
        return { ...obj } as any;
    }
};

const sanitizeNodes = (nodes: AppNode[]): AppNode[] => {
    // 1. First attempt a safe deep clone to break references
    const cleanNodes = safeDeepClone(nodes);
    
    return cleanNodes.map((node: any) => {
        // 2. Explicitly remove internal ReactFlow fields that cause issues on restore
        const internalFields = [
            'positionAbsolute', 'measured', 'handleBounds', 
            'dragging', 'selected', 'resizing', 'width', 'height', 'z'
        ];
        
        internalFields.forEach(field => delete node[field]);
        
        // 3. Ensure position is valid
        if (!node.position || typeof node.position.x !== 'number' || typeof node.position.y !== 'number') {
            console.warn(`Node ${node.id} has invalid position, resetting to {0,0}.`);
            node.position = { x: 0, y: 0 };
        }

        return node as AppNode;
    });
};

const validateGraph = (nodes: AppNode[]): string[] => {
    console.groupCollapsed("Graph Validation Trace");
    const errors: string[] = [];
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    // Check 1: Parent existence & Self-ref
    nodes.forEach(n => {
        if (n.parentNode) {
            if (n.parentNode === n.id) {
                const msg = `CRITICAL: Self-Reference detected. Node ${n.id} is its own parent.`;
                console.error(msg);
                errors.push(msg);
            }
            if (!nodeMap.has(n.parentNode)) {
                console.warn(`Orphaned Node: ${n.id} references missing parent '${n.parentNode}'`);
            }
        }
    });

    // Check 2: Deep Cycle Detection
    // Trace up from every node to ensure no infinite loops in parent hierarchy
    nodes.forEach(startNode => {
        if (!startNode.parentNode) return;

        let currentId = startNode.parentNode;
        const path = [startNode.id];
        const visited = new Set<string>([startNode.id]);
        
        let depth = 0;
        const MAX_DEPTH = 500; // Safety brake

        while (currentId) {
            // Check if we hit a node we've already visited in this chain
            if (visited.has(currentId)) {
                 const cyclePath = [...path, currentId].join(' -> ');
                 const msg = `CRITICAL: Cycle Detected in parent hierarchy: ${cyclePath}`;
                 console.error(msg);
                 errors.push(msg);
                 break;
            }
            
            visited.add(currentId);
            path.push(currentId);
            
            const parent = nodeMap.get(currentId);
            if (!parent) break; // Parent missing, stop tracing
            
            currentId = parent.parentNode;
            
            depth++;
            if (depth > MAX_DEPTH) {
                const msg = `Validation Aborted: Max depth exceeded for node ${startNode.id}. Chain: ${path.join(' -> ')}`;
                console.error(msg);
                errors.push(msg);
                break;
            }
        }
    });

    console.groupEnd();
    return errors;
};

export const useProcessManagement = (
    nodes: AppNode[],
    setNodes: Dispatch<SetStateAction<AppNode[]>>,
    edges: Edge[],
    setEdges: Dispatch<SetStateAction<Edge[]>>,
    initialData?: ProcessDefinition[]
) => {
    const { fitView } = useReactFlow();
    const { addToast } = useToast();
    
    // Determine initial state
    const startingData = (initialData && initialData.length > 0) 
        ? initialData 
        : [{ id: 'proc_1', name: 'New Process', nodes: [], edges: [] } as ProcessDefinition];
    
    // Initialize with a Deep Copy to avoid mutating the source
    const [activeProcessId, setActiveProcessId] = useState<string>(startingData[0].id);
    const [processes, setProcesses] = useState<ProcessDefinition[]>(
        JSON.parse(JSON.stringify(startingData))
    );

    const handleSelectProcess = useCallback((id: string) => {
        if (id === activeProcessId) return;

        console.group(`Switching Process: ${activeProcessId} -> ${id}`);
        try {
            // 1. Save Current State
            const currentNodesSanitized = sanitizeNodes(nodes);
            
            setProcesses(prev => prev.map(p => 
                p.id === activeProcessId 
                    ? { ...p, nodes: currentNodesSanitized, edges } 
                    : p
            ));

            // 2. Find and Clone Target Process
            // We use a deep clone of the target process to ensure we are working on a 
            // fresh copy that we can mutate/fix without affecting the master state yet.
            const rawTargetProc = processes.find(p => p.id === id);
            
            if (!rawTargetProc) {
                console.error(`Process ${id} not found!`);
                addToast({ type: 'error', title: 'Error', message: 'Process not found.' });
                return;
            }

            // Create working copy
            const targetProc = safeDeepClone(rawTargetProc);

            // 3. Validate and Auto-Repair Target Graph
            const graphErrors = validateGraph(targetProc.nodes);
            
            if (graphErrors.length > 0) {
                console.warn("Validation failed. Attempting auto-repair by stripping invalid parentNodes...");
                addToast({ 
                    type: 'warning', 
                    title: 'Graph Auto-Repaired', 
                    message: `Detected ${graphErrors.length} layout issues. Some groupings may be reset.` 
                });
                
                // Identify problematic node IDs from error messages
                targetProc.nodes = targetProc.nodes.map(n => {
                     // Check if this node is involved in any reported error
                     const isProblematic = graphErrors.some(e => e.includes(n.id));
                     
                     if (isProblematic && n.parentNode) {
                         console.warn(`[Auto-Repair] Detaching node ${n.id} from parent ${n.parentNode} to break cycle.`);
                         const { parentNode, ...rest } = n;
                         // When detaching, reset position
                         return { ...rest, position: { x: 0, y: 0 } };
                     }
                     return n;
                });
            }

            // 4. Load Safe State
            const nodesToLoad = sanitizeNodes(targetProc.nodes);
            const edgesToLoad = targetProc.edges;

            setNodes(nodesToLoad);
            setEdges(edgesToLoad);
            setActiveProcessId(id);
            
            // 5. Reset View
            setTimeout(() => {
                fitView({ duration: 500 });
            }, 50);

        } catch (e: any) {
            console.error("Critical Failure Switching Process:", e);
            addToast({ type: 'error', title: 'System Error', message: "Failed to load process: " + e.message });
        } finally {
            console.groupEnd();
        }
    }, [activeProcessId, nodes, edges, processes, setNodes, setEdges, fitView, addToast]);

    const handleCreateProcess = useCallback(() => {
        // Save current
        const currentNodesSanitized = sanitizeNodes(nodes);
        setProcesses(prev => prev.map(p => 
            p.id === activeProcessId 
                ? { ...p, nodes: currentNodesSanitized, edges } 
                : p
        ));

        const newId = `proc_${Date.now()}`;
        const newProcess: ProcessDefinition = {
            id: newId,
            name: `Process ${processes.length + 1}`,
            nodes: [],
            edges: []
        };

        setProcesses(prev => [...prev, newProcess]);
        setNodes([]);
        setEdges([]);
        setActiveProcessId(newId);
        addToast({ type: 'success', title: 'Process Created', message: 'New blank process initialized.' });
    }, [activeProcessId, nodes, edges, processes, setNodes, setEdges, addToast]);

    const handleCreateSubProcess = useCallback(() => {
        // Save current
        const currentNodesSanitized = sanitizeNodes(nodes);
        setProcesses(prev => prev.map(p => 
            p.id === activeProcessId 
                ? { ...p, nodes: currentNodesSanitized, edges } 
                : p
        ));

        const newId = `subproc_${Date.now()}`;
        const newProcess: ProcessDefinition = {
            id: newId,
            name: `Sub Process ${processes.length + 1}`,
            isSubProcess: true,
            nodes: [],
            edges: []
        };

        setProcesses(prev => [...prev, newProcess]);
        setNodes([]);
        setEdges([]);
        setActiveProcessId(newId);
        addToast({ type: 'success', title: 'Sub-Process Created', message: 'New blank sub-process initialized.' });
    }, [activeProcessId, nodes, edges, processes, setNodes, setEdges, addToast]);

    const handleDeleteProcess = useCallback((id: string) => {
        if (processes.length <= 1) {
            addToast({ type: 'warning', title: 'Action Prevented', message: "Cannot delete the last process." });
            return;
        }

        const isDeletingActive = id === activeProcessId;
        const newProcesses = processes.filter(p => p.id !== id);
        setProcesses(newProcesses);

        if (isDeletingActive) {
            const nextProc = newProcesses[0];
            const cleanNextNodes = sanitizeNodes(nextProc.nodes);
            
            setNodes(cleanNextNodes);
            setEdges(JSON.parse(JSON.stringify(nextProc.edges)));
            setActiveProcessId(nextProc.id);
            setTimeout(() => fitView(), 50);
        }
        addToast({ type: 'info', title: 'Process Deleted', message: 'Process definition has been removed.' });
    }, [processes, activeProcessId, setNodes, setEdges, fitView, addToast]);

    const handleUpdateProcess = useCallback((id: string, data: Partial<ProcessDefinition>) => {
        setProcesses(prev => prev.map(p => 
            p.id === id ? { ...p, ...data } : p
        ));
    }, []);

    return {
        processes,
        setProcesses,
        activeProcessId,
        setActiveProcessId,
        handleSelectProcess,
        handleCreateProcess,
        handleCreateSubProcess,
        handleDeleteProcess,
        handleUpdateProcess
    };
};